import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import "@qlik/qlik/src/assets/vendor/slick/slick.css";
import "@qlik/qlik/src/assets/vendor/slick/slick-theme.css";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import * as styles from "./styles.module.css";

const SingleQuoteDark = ({ data }) => {
    const { additionalConfiguration, backgroundColor, backgroundImage, cardItems = [], customId } = data ?? {};
    // State variables for slider navigation and settings
    const [sliderTabNavigation, setSliderTabNavigation] = useState(null);
    const [sliderContentNavigation, setSliderContentNavigation] = useState(null);
    const [sliderTab, setSliderTab] = useState(null);
    const [sliderContent, setSliderContent] = useState(null);
    const [totalSlides, setTotalSlides] = useState(4);
    const [sliderInitialized, setSliderInitialized] = useState(false);

    // Set slider navigation on initialization
    useEffect(() => {
        setSliderTabNavigation(sliderTab);
        setSliderContentNavigation(sliderContent);
        setSliderInitialized(true);
    }, [sliderTab, sliderContent]);

    // Set total slides based on card items length
    useEffect(() => {
        setTotalSlides(cardItems.length);
    }, [cardItems]);

    // Slider tab settings
    const settingsSliderTab = {
        slidesToShow: totalSlides,
        slidesToScroll: 1,
        arrows: false,
        swipeToSlide: true,
        focusOnSelect: true,
        variableWidth: true,
        infinite: false,
    };

    // Slider content settings
    const settingsSliderContent = {
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        cssEase: "linear",
        swipeToSlide: true,
        speed: 700,
        infinite: false,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    arrows: false,
                },
            },
        ],
    };

    // Destructure data
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration, "py-15 lg:py-30");
    const { sectionColorMapping } = getColorClassName(backgroundColor);
    const sectionBgImage = backgroundImage?.[0]?.secure_url;
    return (
        <section
            className={`${styles.ThumbnailQuoteSliderSection} ${sectionSpacingClassName} ${sectionColorMapping.bgColor}`}
            style={{ backgroundImage: sectionBgImage ? `url(${sectionBgImage})` : "none" }}
            id={customId}
        >
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <div className="ThumbnailQuoteSlider slick-arrow-top-right">
                            <Slider
                                className={`ThumbnailQuoteSliderContent ${
                                    !sliderInitialized ? "sliderNotInitialized" : ""
                                }`}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...settingsSliderContent}
                                asNavFor={sliderContentNavigation}
                                ref={(slider) => setSliderTab(slider)}
                            >
                                {cardItems?.map((slide) => (
                                    <div className="slideWrapper h-full" key={slide.id}>
                                        <div
                                            className="overflow-hidden !flex flex-col h-full gap-5 rounded-sm bg-blue bg-blend-color-dodge p-7.5 pr-7.75 md:flex-row-reverse max-md:!bg-none bg-no-repeat bg-right-top bg-single-quote-shape 
                      md:justify-between lg:gap-10 lg:p-10"
                                            style={{
                                                backgroundImage: slide?.media?.[1]
                                                    ? `url(${slide?.media?.[1]?.desktopMedia?.[0]?.secure_url})`
                                                    : "none",
                                            }}
                                        >
                                            {slide?.media?.[0] && (
                                                <div className="relative -mr-7.5 h-[282px] rounded-tl rounded-bl contain-paint shadow-xlLight md:flex-[0_0_302px] lg:h-[367px] lg:mt-5 lg:-mr-10 lg:ml-11 xl:flex-[0_0_402px]">
                                                    <Media
                                                        media={slide?.media?.[0]}
                                                        className="w-full h-full object-cover max-md:object-left-top"
                                                    />
                                                </div>
                                            )}
                                            <div className="flex justify-between flex-col gap-15 max-md:h-[calc(100%-302px)] lg:gap-21">
                                                <blockquote className="relative flex flex-col gap-y-7.75 lg:gap-7.5">
                                                    {slide?.description?.description && (
                                                        <p
                                                            className={`text-md leading-tight tracking-tight4 font-semibold text-white mt-7.75 indent-1 lg:mt-10.5 lg:indent-5 lg:text-3xl lg:tracking-tight7
                            before:relative before:green-quote-large before:content-[''] before:-left-[5px] before:-top-[23px] before:scale-75 
                            lg:before:scale-100 lg:before:-left-4 lg:before:-top-9
                            after:relative after:white-quote-small after:content-[''] after:bottom-1.75 after:right-0.25 after:scale-75
                            lg:after:scale-100 lg:after:bottom-3.25`}
                                                        >
                                                            {slide.description.description}
                                                        </p>
                                                    )}
                                                    {slide?.title && (
                                                        <footer className="flex flex-col gap-y-3 text-base text-white title-5 lg:text-xl lg:gap-2.25">
                                                            {slide?.title}
                                                            {slide?.subtitle && (
                                                                <cite className="not-italic text-xs lg:text-sm leading-none font-normal tracking-normal">
                                                                    {slide?.subtitle}
                                                                </cite>
                                                            )}
                                                        </footer>
                                                    )}
                                                </blockquote>
                                                {slide?.cta?.map((button) => (
                                                    <Cta key={button.id} data={button} className="max-md:w-full" />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </Slider>
                            <Slider
                                className={`ThumbnailQuoteSliderTab mt-2.5 lg:mt-10 ${
                                    !sliderInitialized ? "sliderNotInitialized" : ""
                                }`}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...settingsSliderTab}
                                asNavFor={sliderTabNavigation}
                                ref={(slider) => setSliderContent(slider)}
                            >
                                {cardItems?.map((slide) => (
                                    <div
                                        key={slide.id}
                                        className="slideWrapper rounded-full contain-paint w-full h-full"
                                    >
                                        {slide?.mediaIcon && (
                                            <Media
                                                media={slide?.mediaIcon}
                                                className="w-full h-full object-cover object-top grayscale"
                                            />
                                        )}
                                    </div>
                                ))}
                            </Slider>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SingleQuoteDark;
